import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import {
	ShopOrderLineFormApi,
	ShopOrderLineFormApi_InitData,
} from "src/api/generated/erp/production/api/shopOrderBom/shopOrderLineFormApi.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { ShopOrderLine } from "src/api/generated/erp/db/types/tables/shopOrderLine.ts";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { SitePartQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartQueryApi.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import i18n from "i18next";
import { concatWithPipe } from "src/utils/strings.tsx";
import { getPartRevisionLineAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/partRevisionLineAcquisitionMethod.ts";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { FetchAsyncOptionParams } from "src/components/common/inputFields/AsyncSelectField.tsx";
import { FormLazySelectField } from "src/components/common/forms/fields/FormLazySelectField.tsx";
import { SupplierQueryApi } from "src/api/generated/erp/purchase/suppliers/api/supplierQueryApi.ts";
import { SupplierPartView } from "src/api/generated/erp/db/types/tables/supplierPartView.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ShopOrderLineFormProps extends FormCommonProps<number> {
	shopOrderLineId: number | null;
	shopOrderId: number;
}

interface FormValues extends ShopOrderLine {
	part: PartView;
}

export const ShopOrderLineForm = ({
	shopOrderLineId,
	shopOrderId,
	onCompleted,
	onFormEdited,
}: ShopOrderLineFormProps) => {
	return (
		<AsyncForm<ShopOrderLineFormApi_InitData, FormValues, number>
			columns={2}
			fetch={async () =>
				ShopOrderLineFormApi.getInitData({
					shopOrderId: shopOrderId,
					shopOrderLineId: shopOrderLineId,
				})
			}
			getDefaultValues={(data) => {
				return data.shopOrderLine != null && data.part != null ?
						{
							part: data.part,
							...data.shopOrderLine,
						}
					:	{
							referenceNo: data.defaultReferenceNo,
						};
			}}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			submit={submitForm}
			agentContext={{
				key: defineAgentUiContextKey("erp.production.shopOrderLine.form"),
				title: i18n.t("shop_order_line"),
				data: {
					shopOrderId,
					shopOrderLineId: shopOrderLineId ?? null,
				},
			}}
			render={(params) => (
				<FormContent
					shopOrderLineId={shopOrderLineId}
					shopOrderId={shopOrderId}
					onCompleted={onCompleted}
					{...params}
				/>
			)}
		/>
	);

	async function submitForm(values: FormValues) {
		if (shopOrderLineId == null) {
			return await ShopOrderLineFormApi.insert({
				shopOrderLine: values,
				shopOrderId: shopOrderId,
				partId: values.part.partId,
			});
		} else {
			const { part, ...shopOrderLine } = values;
			return await ShopOrderLineFormApi.update({ request: { ...shopOrderLine, partId: part.partId } });
		}
	}
};

interface FormContentProps
	extends ShopOrderLineFormProps, AsyncFormContentParams<ShopOrderLineFormApi_InitData, FormValues> {}

const FormContent = ({
	data: { siteId, shopOrderOperationOptions, shopOrderLine },
	control,
	setValue,
	watch,
}: FormContentProps) => {
	const isNewRecord = shopOrderLine == null;
	const acquisitionMethod = watch("acquisitionMethod");
	return (
		<>
			<FormNumberField
				control={control}
				name={"referenceNo"}
				label={i18n.t("reference_no")}
				rules={requireRule()}
			/>
			<FormAsyncSelectField
				control={control}
				name={"part"}
				autoFocus={isNewRecord}
				label={i18n.t("part")}
				fetchOptions={async ({ searchQuery, currentSelection }: FetchAsyncOptionParams<number>) =>
					await SitePartQueryApi.getPartSelectionOptions({
						searchQuery: searchQuery,
						currentPartId: currentSelection,
						siteId: siteId,
					})
				}
				formValueType={"option"}
				getOptionKey={(option) => option.partId}
				getOptionLabel={(option) => option.partLongName}
				rules={requireRule()}
				onChange={(part: PartView | null) => {
					setValue(
						"acquisitionMethod",
						part == null ? null
						: part.acquisitionMethod === "PURCHASE_DIRECT" ? null
						: part.acquisitionMethod === "SPLIT_BOM" ? null
						: part.acquisitionMethod,
					);
				}}
			/>
			<FormNumberField name={"quantity"} label={i18n.t("quantity")} control={control} rules={requireRule()} />
			<FormSelectField
				control={control}
				name={"operationId"}
				label={i18n.t("operation")}
				options={shopOrderOperationOptions}
				getOptionKey={(o) => o.shopOrderOperationId}
				getOptionLabel={(o) => concatWithPipe(o.operationNo, o.operationDescription)}
			/>
			<FormEnumSelectField
				control={control}
				name={"acquisitionMethod"}
				label={i18n.t("acquisition_method")}
				options={getPartRevisionLineAcquisitionMethodLabels()}
				rules={requireRule()}
			/>
			{acquisitionMethod === "PURCHASE_TRANSIT" && (
				<FormLazySelectField
					control={control}
					name={"supplierPartId"}
					label={i18n.t("supplier")}
					fetchOptions={async () => {
						const part = watch("part");
						if (part == null) return [];
						return await SupplierQueryApi.getPartSupplierOptions({ partId: part.partId });
					}}
					getOptionKey={(o: SupplierPartView) => o.supplierPartId}
					getOptionLabel={(o) => o.supplierName}
					helperText={i18n.t("shop_order_line_form.supplier_helper_text")}
				/>
			)}
			<FormTextField control={control} name={"note"} label={i18n.t("note")} spanGridColumns />
		</>
	);
};
